import { getCadFileShortSharingId, getInvitedEmails } from '@api/cadFile.js';
import { getFolderInvitations, getProjectSharingId } from '@api/projects.js';
import { isPartOfOrg } from '@api/user.js';
import accessControlInstance, { AccessControl } from '@auth/accessControl.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import AccessControls from '@components/Modals/ShareModal/AccessControls.js';
import EmailInput from '@components/Modals/ShareModal/EmailInput.js';
import InvitedList from '@components/Modals/ShareModal/InvitedList.js';
import { OHIVE_COLORS } from '@constants/global.js';
import { LinkIcon } from '@icons/index.js';
import ActiveResourceState from '@states/ActiveResource.js';
import ProjectsState from '@states/Projects.js';
import UserState from '@states/User.js';
import { MemberState } from '@states/Viewer';
import { UIButton, UIDiv, UIText } from '@ui';

export default class ShareModal extends Modal {
  constructor(givenActiveResource = null, resourceType = 'task') {
    const title =
      i18next.t('menubar.share.share_title') +
      ' ' +
      resourceType.charAt(0).toUpperCase() +
      resourceType.slice(1);

    super(title, {
      showCancel: false,
      showConfirm: false,
      modalContentClass: '!px-0 pb-0 pt-6 !overflow-visible',
      contentContainerClass: 'w-full',
      showCloseIcon: true,
    });

    const accessControl = new AccessControl({
      subscribeToGlobal: false,
      initialResource: givenActiveResource,
      initialResourceType: resourceType,
      initialContext: {
        spaceType:
          this.selectedProjectType === 'team' ? 'organization' : 'personal',
      },
    });

    this.resourceType = resourceType;
    this.givenActiveResource = givenActiveResource;
    this.accessControl = givenActiveResource
      ? accessControl
      : accessControlInstance;
    this.state = {
      publicAccessLevel: {},
      commentsEnabled: false,
      resourceId: null,
      resourceSharingId: null,
      isOrgUsersVisible: true,
      isPublic: false,
      hasOrg: false,
      isUserAdmin: false,
      isProject: resourceType === 'project',
    };

    this.invitedEmails = [];
    this.pendingEmails = [];
    this.orgUsers = MemberState.members || [];
    this.shortSharingId = '';

    this.emailInputComponent = null;
    this.invitedListComponent = null;

    this.initPromise = this.initFromActiveResource();
  }

  async initFromActiveResource() {
    const resource =
      this.givenActiveResource || ActiveResourceState.activeResource.resource;
    const context = this.givenActiveResource
      ? this.givenActiveResource.context
      : ActiveResourceState.activeResource.context;
    if (!resource) {
      console.error('No active resource available');
      return;
    }

    this.state.resourceId =
      resource.cadFileId ||
      resource.cadFileID ||
      resource.id ||
      resource.folderId;
    this.state.resourceSharingId = resource.sharingId;
    this.state.commentsEnabled =
      resource.commentsEnabled || context?.commentsEnabled || false;
    this.state.publicAccessLevel = resource.cadfileAccessLevel || {};
    this.state.isPublic =
      resource.isPublic ||
      (this.state.publicAccessLevel.level &&
        this.state.publicAccessLevel.level !== 'Restricted');
    this.state.isUserAdmin = this.accessControl.hasPermission('update');

    try {
      try {
        if (this.state.isProject) {
          const invitationsResponse = await getFolderInvitations(
            this.state.resourceId,
          );
          if (invitationsResponse && invitationsResponse.data) {
            this.invitedEmails = invitationsResponse.data.response || [];
          }
        } else {
          const updatedEmails = await getInvitedEmails(this.state.resourceId);
          if (updatedEmails && updatedEmails.response) {
            this.invitedEmails = updatedEmails.response;
          }
        }
      } catch (error) {
        console.error('Failed to fetch invitations:', error);
        this.invitedEmails = [];
      }

      const belongsToOrg =
        resource.organizationId || resource.model_folder?.organizationId;
      const hasOrg = await isPartOfOrg(
        belongsToOrg,
        accessControlInstance.user?.uid,
      );
      this.state.hasOrg = hasOrg;

      if (belongsToOrg && hasOrg) {
        // await this.fetchOrgUsers(belongsToOrg);
      } else {
        this.state.isOrgUsersVisible = false;
      }

      if (!this.state.isProject) {
        const response = await getCadFileShortSharingId(this.state.resourceId);
        this.shortSharingId = response.response;
      } else {
        const response = await getProjectSharingId(this.state.resourceId);
        this.shortSharingId = response.response;
      }
    } catch (error) {
      console.error('Initialization failed:', error);
      if (!error.code === 'NOT_AUTHORIZED') {
        toast.error(i18next.t('menubar.share.toast.resource_not_found'));
      }
    }
  }

  render() {
    if (!this._cachedResource) {
      this._cachedResource =
        this.givenActiveResource || ActiveResourceState.activeResource.resource;
    }
    const resource = this._cachedResource;
    const resourceName = resource.name || 'Unnamed Resource';

    let container;
    if (!this.contentContainer) {
      container = new UIDiv();
      container.dom.tabIndex = 0;
      this.contentContainer = container;
    } else {
      container = this.contentContainer;
      container.setInnerHTML('');
    }
    container.setClass('w-full max-w-xl p-6 pt-0 rounded-bl-xl rounded-br-xl');

    const titleContainer = new UIDiv();
    titleContainer.setClass(
      'flex truncate items-center justify-center max-w-full',
    );

    const titleText = new UIText(`"${resourceName}"`);
    titleText.setClass('truncate max-w-full text-center');
    titleContainer.add(titleText);

    if (!this.state.isProject) {
      const titleSection = new UIDiv();
      const isLongTitle = resourceName.length > 30;
      titleSection.setClass(
        `mb-6 text-lg items-center px-4 ${
          isLongTitle
            ? 'flex flex-col gap-4'
            : 'sm:flex sm:flex-col md:grid md:grid-cols-[1fr_2fr_1fr]'
        }`,
      );
      titleSection.setId('share-dialog-title-section');

      const leftSection = new UIDiv();
      const rightSection = new UIDiv();
      rightSection.setClass(
        `flex ${isLongTitle ? 'justify-center w-full' : 'justify-center md:justify-end'} text-base text-[${OHIVE_COLORS.primary}]`,
      );

      const copyLinkButton = new UIButton(i18next.t('menubar.share.copy_link'));
      const copyLinkButtonIcon = new LinkIcon({
        color: 'transparent',
        size: 15,
        strokeColor: OHIVE_COLORS.primary,
      });
      copyLinkButtonIcon.setClass('w-4 h-4');
      copyLinkButton.add(copyLinkButtonIcon);
      copyLinkButton.setClass(
        'copy-link-button hover:opacity-80 py-2 px-2 rounded transition-all duration-300 transform scale-100 flex flex-row-reverse gap-1.5 items-center',
      );

      copyLinkButton.onClick(() => {
        const url = `${window.location.origin}/s?id=${this.shortSharingId}`;
        navigator.clipboard.writeText(url);
        toast.success(i18next.t('menubar.share.url_copied'));
      });

      if (this.resourceType === 'task') {
        rightSection.add(copyLinkButton);
      }
      titleSection.add(leftSection, titleContainer, rightSection);
      container.add(titleSection);
    } else {
      const titleSection = new UIDiv();
      titleSection.setClass(
        'mb-6 text-lg items-center px-4 md:grid md:grid-cols-[1fr_2fr_1fr]',
      );
      titleSection.setId('share-dialog-title-section');

      const leftSection = new UIDiv();
      const rightSection = new UIDiv();
      rightSection.setClass(
        'flex justify-center md:justify-end text-base text-[' +
          OHIVE_COLORS.primary +
          ']',
      );

      const copyProjectLinkButton = new UIButton(
        i18next.t('menubar.share.copy_link') || 'Copy Link',
      );
      const copyLinkButtonIcon = new LinkIcon({
        color: 'transparent',
        size: 15,
        strokeColor: OHIVE_COLORS.primary,
      });
      copyLinkButtonIcon.setClass('w-4 h-4');
      copyProjectLinkButton.add(copyLinkButtonIcon);
      copyProjectLinkButton.setClass(
        'copy-link-button hover:opacity-80 py-2 px-2 rounded transition-all duration-300 transform scale-100 flex flex-row-reverse gap-1.5 items-center',
      );

      copyProjectLinkButton.onClick(() => {
        const url = `${window.location.origin}/projects?projectId=${this.shortSharingId}`;
        navigator.clipboard.writeText(url);
        toast.success(
          i18next.t('menubar.share.url_copied') || 'URL copied to clipboard',
        );
      });

      rightSection.add(copyProjectLinkButton);
      titleSection.add(leftSection, titleContainer, rightSection);
      container.add(titleSection);
    }

    const emailInput = new EmailInput({
      accessControl: this.accessControl,
      emails: this.pendingEmails,
      invitedEmailsList: this.invitedEmails,
      resourceId: this.state.resourceId,
      hasOrg: this.state.hasOrg,
      isPublic: this.state.isPublic,
      isProject: this.state.isProject,
      currentUserEmail: UserState.user?.email,
      orgUsers: this.orgUsers,
      onEmailsChange: updatedEmails => {
        this.pendingEmails = updatedEmails;
      },
      onSendInvitesComplete: async () => {
        try {
          const fetch = this.state.isProject
            ? getFolderInvitations(this.state.resourceId)
            : getInvitedEmails(this.state.resourceId);

          const response = await fetch;
          const newInvites = this.state.isProject
            ? response?.data?.response || []
            : response?.response || [];

          const existingEmails = this.invitedEmails.map(e =>
            e.email.toLowerCase(),
          );
          const newlyAdded = newInvites.filter(
            invite => !existingEmails.includes(invite.email.toLowerCase()),
          );

          this.invitedEmails = newInvites;
          this.pendingEmails = [];

          if (this.invitedListComponent?.container) {
            newlyAdded.forEach(invite =>
              this.invitedListComponent.addUserItem(invite),
            );
          } else {
            this.render();
          }
        } catch (error) {
          console.error('Failed to refresh invited emails:', error);
        }
      },
    });

    this.emailInputComponent = emailInput;

    const invitedList = new InvitedList({
      accessControl: this.accessControl,
      orgUsers: this.orgUsers,
      emails: this.invitedEmails,
      isOrgUsersVisible: this.state.isOrgUsersVisible,
      currentUserId: accessControlInstance.user?.uid,
      resourceOwnerId: resource.uid,
      resourceId: this.state.resourceId,
      hasOrg: this.state.hasOrg,
      isProject: this.state.isProject,
      onEmailsChange: updatedEmails => {
        this.invitedEmails = updatedEmails;
      },
    });
    this.invitedListComponent = invitedList;

    const accessControls = new AccessControls({
      accessControl: this.accessControl,
      isPublic: this.state.isPublic,
      commentsEnabled: this.state.commentsEnabled,
      resourceName: resourceName,
      shortSharingId: this.shortSharingId,
      currentAccessLevel: this.state.publicAccessLevel.level,
      resourceSharingId: this.state.resourceSharingId,
      resourceId: this.state.resourceId,
      hasOrg: this.state.hasOrg,
      isProject: this.state.isProject,
      onStateChange: stateChanges => {
        Object.assign(this.state, stateChanges);

        if (!this.givenActiveResource && ActiveResourceState.activeResource) {
          ActiveResourceState.setActiveResource({
            ...ActiveResourceState.activeResource,
            resource: {
              ...ActiveResourceState.activeResource.resource,
              ...stateChanges,
            },
            context: {
              ...ActiveResourceState.activeResource.context,
              ...stateChanges,
            },
          });
        }

        if (this.givenActiveResource && ProjectsState.projects) {
          const { tasks, folders } = ProjectsState.projects;
          if (this.resourceType === 'task') {
            const updatedTasks = tasks.map(task =>
              task.id === this.state.resourceId
                ? { ...task, ...stateChanges }
                : task,
            );
            ProjectsState.setProjects({
              ...ProjectsState.projects,
              tasks: updatedTasks,
            });
          } else {
            const updatedFolders = folders.map(folder =>
              folder.id === this.state.resourceId
                ? { ...folder, ...stateChanges }
                : folder,
            );
            ProjectsState.setProjects({
              ...ProjectsState.projects,
              folders: updatedFolders,
            });
          }
        }
      },
    });
    if (this.accessControl.hasPermission('update')) {
      container.add(
        emailInput.render(),
        invitedList.render(),
        accessControls.render(),
      );
    } else {
      container.add(accessControls.render());
    }

    this.options.content = container;
    return container;
  }

  async promptAsync(parentElement) {
    await this.initPromise;
    this.render();
    return this.show(parentElement ? `#${parentElement.id}` : null);
  }
}
